// engine-exception: auth/onboarding flow, not a resource screen — stays hand-written (blueprint §1: web/src/pages is legacy)
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';
import { AuthCard, AuthScene, Stepper } from '../components/authui';
import { SignedInAs } from './OnboardingGate';

// A signed-in user with no tenant ("new"): offer to found an organization for their email
// domain. POST /v1/onboarding starts provisioning; the gate then polls forward to domain
// verification. All policy lives in the backend — we surface its Problem+JSON.

const TITLE_HINTS: Record<string, string> = {
  public_email: 'That’s a personal email. Sign out and use your work email to found an organization.',
  domain_pending: 'Someone at your company has already started this. Ask them to invite you.',
  already_onboarded: 'You already belong to an organization.',
};

export function CreateOrgScreen({ email, domain }: { email: string; domain: string }) {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [domainTaken, setDomainTaken] = useState(false);

  async function create() {
    setBusy(true);
    setError(null);
    setDomainTaken(false);
    try {
      await api.request('/v1/onboarding', { method: 'POST' });
      await queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    } catch (e) {
      if (e instanceof ApiError) {
        const title = (e.problem as { title?: string } | undefined)?.title;
        if (title === 'domain_taken') {
          setDomainTaken(true);
          setError(e.message || 'Your company already has an organization.');
        } else {
          setError((title && TITLE_HINTS[title]) || e.message || 'Could not create your organization.');
        }
      } else {
        setError(e instanceof Error ? e.message : 'Could not create your organization.');
      }
      setBusy(false);
    }
  }

  return (
    <AuthScene>
      <AuthCard footer={<SignedInAs />}>
        <Stepper current={1} />
        <div className="auth-head">
          <h1>Create your organization</h1>
          <p>
            You’re founding the NovaPSA workspace for <span className="kbd-domain">{domain}</span>.
          </p>
        </div>

        <p className="muted" style={{ fontSize: 'var(--fs-13)', lineHeight: 1.5 }}>
          As <strong>{email}</strong>, you’ll be the owner. Next you’ll verify you control{' '}
          <strong>{domain}</strong> with a quick DNS record — then invite your team.
        </p>

        <button className="btn primary block lift" style={{ marginTop: 'var(--sp-4)' }} onClick={() => void create()} disabled={busy}>
          {busy ? 'Creating…' : `Create ${domain}`}
        </button>

        {error && (
          <div className="auth-msg err" style={{ marginTop: 'var(--sp-3)' }}>
            <span>
              ⚠ {error}
              {domainTaken && <div style={{ marginTop: 6 }}>Wrong account? Sign out below and try another.</div>}
            </span>
          </div>
        )}
      </AuthCard>
    </AuthScene>
  );
}
